import { useState } from 'react';
import { Bell, AlertCircle, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEngagementAlerts, EngagementAlert, AlertSeverity } from '@/hooks/use-engagement-alerts';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface EngagementAlertsCardProps {
  clientId: string;
  className?: string;
}

function getAlertIcon(type: AlertSeverity) {
  switch (type) {
    case 'critical':
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-warning" />;
    default:
      return <Info className="h-4 w-4 text-info" />;
  }
}

function getAlertBg(type: AlertSeverity): string {
  switch (type) {
    case 'critical':
      return 'bg-destructive/10 border-destructive/30';
    case 'warning':
      return 'bg-warning/10 border-warning/30';
    default:
      return 'bg-info/10 border-info/30';
  }
}

function AlertItem({ alert }: { alert: EngagementAlert }) {
  return (
    <div className={cn('p-3 rounded-lg border', getAlertBg(alert.type))}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{getAlertIcon(alert.type)}</div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{alert.title}</p>
          <p className="text-sm text-muted-foreground mt-0.5">{alert.description}</p>
          {alert.actionLabel && (
            <Button variant="link" size="sm" className="h-auto p-0 mt-1 text-primary">
              {alert.actionLabel}
            </Button>
          )}
        </div>
        {alert.metric !== undefined && (
          <Badge variant="outline" className="flex-shrink-0">
            {alert.metric}
          </Badge>
        )}
      </div>
    </div>
  );
}

export function EngagementAlertsCard({ clientId, className }: EngagementAlertsCardProps) {
  const { result, loading } = useEngagementAlerts(clientId);
  const [isExpanded, setIsExpanded] = useState(true);

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-36" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return null;
  }

  const hasAlerts = result.alerts.length > 0;
  const totalAlerts = result.criticalCount + result.warningCount + result.infoCount;

  // Show only first 3 when collapsed
  const displayAlerts = isExpanded ? result.alerts : result.alerts.slice(0, 3);

  return (
    <Card className={cn('border', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Bell className={cn('h-5 w-5', hasAlerts ? 'text-warning' : 'text-success')} />
            Engagement Alerts
          </span>
          <div className="flex items-center gap-2">
            {result.criticalCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {result.criticalCount} Critical
              </Badge>
            )}
            {result.warningCount > 0 && (
              <Badge variant="default" className="bg-warning/15 text-warning text-xs">
                {result.warningCount} Warning
              </Badge>
            )}
            {result.infoCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {result.infoCount} Info
              </Badge>
            )}
            {!hasAlerts && (
              <Badge variant="secondary" className="bg-success/15 text-success text-xs">
                No Issues
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasAlerts ? (
          <p className="text-sm text-muted-foreground">
            No engagement concerns detected. Client is tracking well across all metrics.
          </p>
        ) : (
          <div className="space-y-2">
            {displayAlerts.map(alert => (
              <AlertItem key={alert.id} alert={alert} />
            ))}

            {totalAlerts > 3 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full mt-2 flex items-center gap-1"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Show {totalAlerts - 3} More
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
